/**
 * Text measurement for chart labels: the Sankey label lane and the pace chart's y axis.
 *
 * Column widths and margins are derived from the labels that will actually be painted, so
 * the measurement has to use the font those labels inherit rather than a
 * hardcoded family: the app ships Geist (src/app/[locale]/layout.tsx), whose glyph
 * advances differ from the generic sans-serif a guessed font string would fall
 * back to, and a column sized from the wrong font is wrong in a way that still
 * looks plausible in review.
 */

export interface LabelFont {
  /** Computed `font-family` of a rendered label, resolved list and all. */
  family: string;
  /** Computed `letter-spacing` in px; canvas measureText does not apply it. */
  letterSpacing: number;
}

export type MeasureLabel = (text: string, fontSize: number, fontWeight: number) => number;

/**
 * Average glyph width as a fraction of the font size, used only where no canvas
 * exists: the server render and jsdom. Deliberately generous, so a column
 * derived from it comes out too wide rather than too narrow.
 */
const CHAR_WIDTH_RATIO = 0.68;

export const estimateLabelWidth: MeasureLabel = (text, fontSize) =>
  text.length * fontSize * CHAR_WIDTH_RATIO;

let sharedContext: CanvasRenderingContext2D | null | undefined;

function canvasContext(): CanvasRenderingContext2D | null {
  if (sharedContext === undefined) {
    try {
      sharedContext = document.createElement('canvas').getContext('2d');
    } catch {
      // jsdom without the canvas package logs "not implemented" and returns null
      // rather than throwing; other headless environments do throw, so both paths
      // land on the ratio estimate.
      sharedContext = null;
    }
  }
  return sharedContext;
}

/**
 * Measures with a real canvas when one is available, falling back to the ratio
 * estimate otherwise. Results are memoized per text/size/weight because every
 * label is measured on each layout pass and the strings repeat across renders.
 */
export function createLabelMeasurer(font: LabelFont | null): MeasureLabel {
  if (!font || typeof document === 'undefined') return estimateLabelWidth;

  const context = canvasContext();
  if (!context) return estimateLabelWidth;

  const cache = new Map<string, number>();
  return (text, fontSize, fontWeight) => {
    const key = `${fontWeight}|${fontSize}|${text}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    context.font = `${fontWeight} ${fontSize}px ${font.family}`;
    const width = context.measureText(text).width + text.length * font.letterSpacing;
    cache.set(key, width);
    return width;
  };
}

/** Reads the font off a mounted element that inherits exactly what the labels do. */
export function readLabelFont(element: Element): LabelFont {
  const style = getComputedStyle(element);
  const letterSpacing = Number.parseFloat(style.letterSpacing);
  return {
    family: style.fontFamily,
    letterSpacing: Number.isFinite(letterSpacing) ? letterSpacing : 0,
  };
}
